import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';
import { useOffers } from "./../hooks/useOffers";
import {
    Tag,
    Users,
    ShoppingBag,
    IndianRupee,
    Plus,
} from "lucide-react";

import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { MobileHeader } from "./MobileHeader";

function StatCard({
    title,
    value,
    subtitle,
    icon: Icon,
    color,
}) {
    return (
        <div className="offer-stat-card">

            <div className={`offer-stat-icon ${color}`}>
                <Icon size={24} />
            </div>

            <div className="offer-stat-content">

                <div className="offer-stat-title">
                    {title}
                </div>

                <div className="offer-stat-value">
                    {value}
                </div>

                <div className="offer-stat-subtitle">
                    {subtitle}
                </div>

            </div>

            {/* <ChevronRight
                size={18}
                className="offer-stat-arrow"
            /> */}

        </div>
    );
}

function StatusBadge({ status }) {
    return (
        <span
            className={`status-badge ${status?.toLowerCase()}`}
        >
            {status}
        </span>
    );
}

export function Offers() {

    const [search, setSearch] = useState("");
    const navigate = useNavigate();

    const {
        data,
        isLoading,
        error,
    } = useOffers();

    if (isLoading) {
        return (

            <div className="page-state">

                <div className="page-state-spinner" />

                <h2>

                    Loading...

                </h2>

                <p>

                    We're fetching your latest data.

                </p>

            </div>

        );
    }

    if (error) {
        return (

            <div className="page-state">

                <div className="page-state-icon">

                    !

                </div>

                <h2>

                    Something went wrong

                </h2>

                <p>

                    We couldn't load this page. Please try refreshing or come back in a moment.

                </p>

                <button

                    className="primary-btn"

                    onClick={() => window.location.reload()}

                >

                    Try Again

                </button>

            </div>

        );
    }

    const stats = [
        {
            title: "Active Offers",
            value: data.activeOffers,
            subtitle: "Currently running",
            icon: Tag,
            color: "blue",
        },
        {
            title: "Customers Acquired",
            value: data.customersAcquired,
            subtitle: "Across all offers",
            icon: Users,
            color: "green",
        },
        {
            title: "Total Redemptions",
            value: data.totalRedemptionsThisMonth,
            subtitle: "This month",
            icon: ShoppingBag,
            color: "orange",
        },
        {
            title: "Total Spend",
            value: data.totalSpendThisMonth,
            subtitle: "This month",
            icon: IndianRupee,
            color: "purple",
        },
    ];

    const filteredOffers = data.offers.filter((offer) => {

        const text =
            `${offer.title} ${offer.description}`.toLowerCase();

        return text.includes(search.toLowerCase());

    });


    return (

        <div className="offers-page">

            <MobileHeader />

            <Sidebar />

            <div className="offers-content">

                <Header
                    title="Offers"
                    subtitle="Create and manage offers to acquire new customers."
                    action={
                        <button
                            className="primary-btn"
                            onClick={() => navigate("/create-offer")}
                        >

                            <Plus size={18} />

                            Create Offer

                        </button>
                    }
                />

                <div className="offer-stats-grid">

                    {stats.map((item) => (

                        <StatCard
                            key={item.title}
                            {...item}
                        />

                    ))}

                </div>

                {/* Filters and table continue in Part 2 */}

                <div className="offers-filters">

                    <div className="search-box">

                        <input
                            type="text"
                            placeholder="Search offers..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />

                    </div>

                    <select className="filter-select">

                        <option>All Status</option>
                        <option>Active</option>
                        <option>Paused</option>
                        <option>Inactive</option>

                    </select>

                    <select className="filter-select">

                        <option>All Channels</option>
                        <option>Online</option>
                        <option>Offline</option>

                    </select>

                    <input
                        type="date"
                        className="date-filter"
                    />

                </div>

                <div className="offers-table-card">

                    <table className="offers-table">

                        <thead>

                            <tr>

                                <th>Offer</th>
                                <th>Status</th>
                                <th>Redemptions</th>
                                <th>Customers</th>
                                <th>Spend</th>
                                {/* <th>Cost / Customer</th> */}
                                <th>Valid Till</th>
                                <th></th>

                            </tr>

                        </thead>

                        <tbody>

                            {filteredOffers.map((offer) => (

                                <tr key={offer.id}>

                                    <td>

                                        <div className="offer-info">

                                            <img
                                                src={offer.image}
                                                alt={offer.title}
                                                className="offer-image"
                                            />

                                            <div>

                                                <div className="offer-title">

                                                    {offer.title}

                                                </div>

                                                <div className="offer-description">

                                                    {offer.description}

                                                </div>

                                            </div>

                                        </div>

                                    </td>

                                    <td>

                                        <StatusBadge
                                            status={offer.status}
                                        />

                                    </td>

                                    <td>

                                        {offer.numberRedeemed}

                                    </td>

                                    <td>

                                        {offer.customers}

                                    </td>

                                    <td>

                                        {offer.spend}

                                    </td>

                                    {/* <td>

                                        {offer.cpa}

                                    </td> */}

                                    <td>

                                        {new Date(offer.validTo).toLocaleString("en-IN", {
                                            day: "2-digit",
                                            month: "short",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}

                                    </td>

                                    <td>

                                        <button className="offer-action">

                                            View

                                        </button>

                                    </td>

                                </tr>

                            ))}

                        </tbody>

                    </table>

                    <div className="offers-pagination">

                        <span>

                            Showing {filteredOffers.length} of {data.offers.length} offers

                        </span>

                        <div className="pagination-buttons">

                            <button>

                                Previous

                            </button>

                            <button className="active primary-btn">

                                1

                            </button>

                            <button>

                                Next

                            </button>

                        </div>

                    </div>

                </div>

            </div>

        </div>

    );

}